import VueType from 'vue-property-decorator';
import { Store } from 'vuex';
import _ from 'lodash';
import accounting from 'accounting';
import { createDecorator } from 'vue-class-component';

export const PropertyGetter = createDecorator((options, key, i) => {
  // component options should be passed to the callback
  // and update for the options object affect the component
  if (options && options.computed) {
    options.computed[key] = createGetterSetter(key);
  }
});

export const formatters = {
  currency(precision = 0) {
    return {
      to(value: string | number) {
        return accounting.formatMoney(value, '£', precision);
      },
      from(value: string) {
        return accounting.unformat(value);
      },
    };
  },
  number(precision = 0) {
    return {
      to(value: number) {
        return accounting.formatNumber(value, precision);
      },
      from(value: string) {
        return accounting.unformat(value);
      },
    };
  },
};

export function createGetterSetter<T>(key: string) {
  return {
    get(this: VueType.Vue) {
      const prop = _.get(this.$store.getters, key) as IProperty<T>;

      if (prop === undefined) {
        throw new Error(`getters.${key} has no value`);
      }

      const { value } = prop;

      return {
        ...prop,
        formatted: resultWith(prop, 'formatter.to', value),
        $v: _.get(this, `$v.${key}.value`),
      };
    },
    set(this: VueType.Vue, value: any) {
      const store = this.$store as Store<any>;
      const prop = _.get(store.getters, key) as IProperty<T>;

      if (prop === undefined) {
        throw new Error(`getters.${key} has no value`);
      }

      store.commit(keyFn(key), resultWith(prop, 'formatter.from', value));
    },
  };
}

export function mapValues(props: string[]) {
  const properties: IProperties = {};

  props.forEach((key) => {
    properties[key] = createGetterSetter(key);
  });

  return properties;
}

export function mapValidations(props: string[]) {
  return function(this: VueType.Vue) {
    const validations: any = {};

    props.forEach((key) => {
      const value = _.get(this.$store.getters, `${key}.validation`);

      if (value) {
        validations[key] = {
          value,
        };
      }
    });

    return validations;
  };
}

export default {
  install(Vue: any, options: any) {
    Vue.prototype.$mapValidations = mapValidations;
  },
};

// Types - Don't edit below
export interface IProperties {
  [x: string]: IPropertyGetterSetter<any>;
}

export interface IPropertyGetterSetter<T> {
  get(): Partial<IProperty<T>>;
  set(value: any): void;
}

export interface IFormatter {
  to(value: any): any;
  to(from: any): any;
}

export interface IProperty<T> {
  value: T;
  formatter: IFormatter;
  validation: any;
  $v: any;
}

function keyFn(key: string): string {
  return `update${_.upperFirst(key)}`;
}

function resultWith(obj: any, path: any, defaultValue: any, ...args: any[]) {
  // We need to know upfront whether or not this
  // is a function we're dealing with.
  const result = _.get(obj, path);
  const isFunc = _.isFunction(result);

  // If this is a function, and there's arguments
  // to apply, then use spread() and bindKey() to
  // return the result of calling the method with arguments.
  // Otherwise, it's just a plain call to result().
  if (isFunc) {
      return result(defaultValue, obj, ...args);
  } else {
      return _.result(obj, path, defaultValue);
  }
}
